import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { CalendarItem } from "./calendarModel";

/**
 * Hook for making a calendar event draggable.
 * Returns ref, listeners, attributes, and isDragging to apply on the element directly.
 */
export function useDraggableEvent(event: CalendarItem) {
  // Heldagshändelser (semester m.m.) schemaläggs inte om via kalendern
  const isDraggable =
    !event.isAllDay &&
    (event.type === "workItem" || event.type === "merged" || event.type === "calendar");

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: event.id,
      data: { event },
      disabled: !isDraggable,
    });

  const dragStyle: React.CSSProperties = {
    ...(transform
      ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
      : {}),
    opacity: isDragging ? 0.4 : undefined,
    zIndex: isDragging ? 100 : undefined,
    cursor: isDraggable ? "grab" : "default",
  };

  return { setNodeRef, listeners, attributes, isDragging, dragStyle };
}

/**
 * Wrapper component for draggable events (used in MonthView where absolute positioning isn't needed).
 */
interface DraggableEventProps {
  event: CalendarItem;
  children: React.ReactNode;
}

const DraggableEvent: React.FC<DraggableEventProps> = ({ event, children }) => {
  const { setNodeRef, listeners, attributes, dragStyle } = useDraggableEvent(event);

  return (
    <div ref={setNodeRef} style={dragStyle} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

export default DraggableEvent;
